import React, { createContext, useEffect, useContext } from 'react';
import { ThemeConfig, ThemeType } from '../types';
import { themes } from '../themes/index';

interface ThemeContextProps {
  currentTheme: ThemeType;
  themeConfig: ThemeConfig;
  changeTheme: (theme: ThemeType) => void;
}

export const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

// Single cohesive neon theme. The provider keeps the same API shape
// (currentTheme / themeConfig / changeTheme) for backward compatibility,
// but there is now only one theme: "neon".
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const themeConfig = themes.neon;

  const applyThemeToDocument = (theme: ThemeConfig) => {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-background', theme.colors.background);
    root.style.setProperty('--color-text', theme.colors.text);
    if (theme.colors.accent) {
      root.style.setProperty('--color-accent', theme.colors.accent);
    }
    root.style.setProperty('--font-family', theme.fontFamily);
    document.body.className = theme.name;
  };

  useEffect(() => {
    applyThemeToDocument(themeConfig);
  }, [themeConfig]);

  // No-op kept so any legacy callers don't break.
  const changeTheme = (_theme: ThemeType) => {
    applyThemeToDocument(themeConfig);
  };

  return (
    <ThemeContext.Provider value={{ currentTheme: 'neon', themeConfig, changeTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
